import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { Button, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select } from "@mui/material";
import * as Yup from "yup";
import { debounce } from "lodash";
import { Tooltip } from "@financeable-com-au/financeable-ui";
import { useGetLocalAndLenderErrors, useShowRequiredFields } from "@/hooks/useRequiredFields";
import { getShouldValidatePersonalFinanceItem } from "@/hooks/useRequiredFields/utils/requiredFieldValues";
import regex from "../../utils/regex";
import {
  ERRORS_MESSAGE,
  expensesTypeList,
  frequencyList,
  SHARE_OF_DISABLED_TOOLTIP,
  validFrequencyList,
} from "../../constants";
import { NumericFormatCustom, currencyMaskedValue } from "../../utils/currencyMaskFormat";
import {
  saveValue,
  createExpense,
  deleteExpense,
  updateExpense,
  userDetailsSelector,
  setRequiredFieldsErrors,
} from "../../store/slices/applicationFormSlice";
import { customerSelector } from "../../store/slices/customerSlice";
import { openSnackbar } from "../../store/slices/snackbarSlice";
import { PercentageFormatCustom } from "../../utils/percentageMaskFormat";
import { getErrorToastPayload } from "../../utils/commonFunctions";

export default function Expenses({
  expenses,
  customerId,
  applicationId,
  personalFinLoaders,
  isDisableEdit,
  isDisabledSharesField,
}) {
  const dispatch = useDispatch();
  const { resetInvalidRequiredFieldsByFieldName } = useShowRequiredFields();
  const { isCustomerLogin } = useSelector(customerSelector);
  const { requireFieldErrors } = useSelector(userDetailsSelector);
  const [expensesList, setExpensesList] = useState(expenses || []);
  const [changeDetected, setChangeDetected] = useState(false);

  const isDisable = isCustomerLogin ? false : isDisableEdit;

  useEffect(() => {
    setExpensesList(expenses || []);
  }, [expenses]);

  const isAddExpenseLoader = personalFinLoaders?.addExpenseLoader && personalFinLoaders?.id === customerId;

  const validExpenseTypesList = expensesTypeList.map((item) => item.value);

  const validationSchema = Yup.object({
    expensesType: Yup.string()
      .oneOf(validExpenseTypesList, "Please select valid expense type")
      .required("Expense type is required"),
    amount: Yup.string().matches(regex.allowOnlyNumber, "Only numbers allowed.").max(10, "Maximum of 7 digits"),
    frequency: Yup.string()
      .oneOf(validFrequencyList, "Please select valid frequency")
      .required("Frequency is required"),
    shareOfExpense: Yup.string()
      .matches(regex.allowOnlyNumber, "Only numbers allowed.")
      .matches(regex.percentageRegex, {
        message: "Percentage should be upto 100%.",
      })
      .max(100, "Maximum of 3 digits"),
  });

  const debouncedValidation = useCallback(
    debounce(async (name, value) => {
      const fieldName = name.split("_").length > 0 ? name.split("_")[0] : name;
      try {
        await validationSchema.validateAt(fieldName, { [fieldName]: value });
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [name]: "",
          }),
        );
      } catch (error) {
        dispatch(
          setRequiredFieldsErrors({
            ...requireFieldErrors,
            [name]: error.message,
          }),
        );
      }
    }, 300),
    [requireFieldErrors],
  );

  const onAddNewExpenses = async ({ expensesType, amount, frequency }) => {
    const data = {
      expensesType: expensesType || "",
      frequency: frequency || "",
      amount: amount || "",
      shareOfExpense: 100,
      applicationId,
      customerId,
    };
    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: customerId,
        },
      }),
    );
    return dispatch(createExpense({ data })).then((res) => {
      if (res?.error) {
        dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
      }
    });
  };

  const removeExpenses = (Element) => {
    const data = {
      expenseId: Element._id,
      application: applicationId,
      customer: customerId,
    };
    delete data._id;
    dispatch(
      saveValue({
        personalFinLoaders: {
          ...personalFinLoaders,
          id: Element._id,
        },
      }),
    );
    dispatch(deleteExpense({ data })).then((res) => {
      if (res?.error) {
        dispatch(openSnackbar(getErrorToastPayload(res, ERRORS_MESSAGE.fetchErrorMsg)));
      }
    });
  };

  const handle = {
    onBlur: (name, value, newID, index) => {
      const isError = getLocalAndLenderError({
        fieldError: requireFieldErrors[`${name}_${customerId}_${index}`],
        fieldName: `${name}_${customerId}_${index}`,
        localFieldName: name,
      });
      // Need to refactor error handling flow if we need to check error for other fields also.
      if (name === "shareOfExpense" && isError) return;

      const temp = [...expensesList];

      temp.map((ele) => {
        if (ele._id === newID) {
          let updatedAssetData = {
            ...ele,
            [name]: value,
            expenseId: newID,
            applicationId,
            customerId,
          };

          delete updatedAssetData._id;

          if (changeDetected) {
            dispatch(updateExpense({ data: updatedAssetData }));
            setChangeDetected(false);
          }
          return updatedAssetData;
        } else {
          return ele;
        }
      });
    },
    onChange: async (field, value, newID) => {
      const name = field.split("_")[0];
      debouncedValidation(field, value);

      const temp = [...expensesList];

      let isChangeDetected = false;

      const updateExpenses = temp.map((ele) => {
        if (!ele._id) {
          const dataWithOutId = {
            ...ele,
            [name]: value,
            applicationId,
            customerId,
          };
          isChangeDetected = true;
          dispatch(createExpense({ data: dataWithOutId }));
        }

        if (ele._id === newID) {
          const updatedExpensesData = {
            ...ele,
            [name]: value,
          };
          if (ele[name] !== value) {
            isChangeDetected = true;
          }
          return updatedExpensesData;
        } else {
          return ele;
        }
      });

      setChangeDetected(isChangeDetected);
      if (isChangeDetected) {
        setExpensesList(updateExpenses);
      }

      // Reset the invalid required fields by field name.
      resetInvalidRequiredFieldsByFieldName({
        section: "customer",
        // For assetTypeValue, we need to reset the assetType field, it is named as assetTypeValue
        // in the local state which is not the same as the assetType field in the backend.
        fieldName: name,
      });
    },

    checkExistingExpense: async (expenses) => {
      for await (let currentExpense of expensesTypeList) {
        const value = currentExpense.value;
        // Check if the value exists in expensesList
        const exists = expenses.some((expense) => expense?.expensesType === value);

        if (!exists) {
          if (value === "Groceries") {
            const data = {
              expensesType: "Groceries",
              amount: "0.00",
              frequency: "Monthly",
              applicationId,
              customerId,
              expenseId: expenses?.[0]?._id,
            };

            await dispatch(updateExpense({ data })).unwrap();
          } else {
            await onAddNewExpenses({
              expensesType: value,
              amount: "0.00",
              frequency: "Monthly",
            });
          }
        }
      }
    },
  };

  const { getCustomerOrEntityLocalAndLenderError } = useGetLocalAndLenderErrors();
  const getLocalAndLenderError = ({ fieldError, fieldName, localFieldName, doNotShowError }) =>
    getCustomerOrEntityLocalAndLenderError({ fieldError, localState: {}, fieldName, localFieldName, doNotShowError });
  // Lender errors only show on validated rows: the first row and any row holding data.
  const validatedRows = expensesList.map((item, index) =>
    getShouldValidatePersonalFinanceItem({ listName: "expenses", item, index }),
  );

  return (
    <>
      <Grid xs={12}>
        <Typography variant="subtitle2" sx={{ fontWeight: "bold" }} style={{ marginBottom: "5px", marginTop: "20px" }}>
          Expenses
        </Typography>
      </Grid>
      <Grid container spacing={2} style={{ marginBottom: "20px" }}>
        {expensesList?.map((ele, index) => (
          <Grid container item xs={12} spacing={1} key={ele?._id}>
            <Grid item xs={12} sm={3}>
              <FormControl
                fullWidth
                variant="filled"
                error={getLocalAndLenderError({
                  fieldName: `expenses.[0].expensesType_${customerId}_${index}`,
                  doNotShowError: !!ele?.expensesType || !validatedRows[index],
                })}
              >
                <InputLabel id={`expense-type-label-${ele?._id}`}>Expense type</InputLabel>
                <Select
                  size="small"
                  labelId={`expense-type-label-${ele?._id}`}
                  id={`expense-type-${ele?._id}`}
                  disabled={isDisable}
                  value={ele?.expensesType}
                  label="Expense type"
                  name={`expensesType_expenses_${customerId}_${index}`}
                  onChange={(event) => {
                    handle.onChange(`expensesType_expenses_${customerId}_${index}`, event.target.value, ele?._id);
                  }}
                  onBlur={() => handle.onBlur("expensesType", ele?.expensesType, ele?._id, index)}
                >
                  {expensesTypeList.map((i) => (
                    <MenuItem value={i.value}>{i.label}</MenuItem>
                  ))}
                </Select>
                {getLocalAndLenderError({
                  fieldName: `expenses.[0].expensesType_${customerId}_${index}`,
                  doNotShowError: !!ele?.expensesType || !validatedRows[index],
                }) && (
                  <FormHelperText>
                    {getLocalAndLenderError({
                      fieldName: `expenses.[0].expensesType_${customerId}_${index}`,
                      doNotShowError: !!ele?.expensesType || !validatedRows[index],
                    })}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                size="small"
                id={`expense-amount-${ele?._id}`}
                type="text"
                inputProps={{ min: 0 }}
                disabled={isDisable}
                label="Expense amount"
                variant="filled"
                style={{ width: "100%" }}
                value={ele?.amount}
                name={`amount_expenses_${customerId}_${index}`}
                error={getLocalAndLenderError({
                  fieldName: `expenses.[0].amount_${customerId}_${index}`,
                  doNotShowError: !!ele?.amount || !validatedRows[index],
                })}
                helperText={getLocalAndLenderError({
                  fieldName: `expenses.[0].amount_${customerId}_${index}`,
                  doNotShowError: !!ele?.amount || !validatedRows[index],
                })}
                InputProps={{
                  inputComponent: NumericFormatCustom,
                }}
                onChange={(event) =>
                  handle.onChange(`amount_expenses_${customerId}_${index}`, event.target.value, ele?._id, index)
                }
                onBlur={() => handle.onBlur("amount", currencyMaskedValue(ele?.amount), ele?._id, index)}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <FormControl
                fullWidth
                variant="filled"
                error={getLocalAndLenderError({
                  fieldName: `expenses.[0].frequency_${customerId}_${index}`,
                  doNotShowError: !!ele?.frequency || !validatedRows[index],
                })}
              >
                <InputLabel id={`expense-frequency-label-${ele?._id}`}>Expense frequency</InputLabel>
                <Select
                  size="small"
                  labelId={`expense-frequency-label-${ele?._id}`}
                  id={`expense-frequency-${ele?._id}`}
                  value={ele?.frequency}
                  disabled={isDisable}
                  label="Expense frequency"
                  name={`frequency_expenses_${customerId}_${index}`}
                  onChange={(event) => {
                    handle.onChange(`frequency_expenses_${customerId}_${index}`, event.target.value, ele?._id, index);
                  }}
                  onBlur={() => handle.onBlur("frequency", ele?.frequency, ele?._id, index)}
                >
                  {frequencyList.map((i) => (
                    <MenuItem value={i.value}>{i.label}</MenuItem>
                  ))}
                </Select>
                {getLocalAndLenderError({
                  fieldName: `expenses.[0].frequency_${customerId}_${index}`,
                  doNotShowError: !!ele?.frequency || !validatedRows[index],
                }) && (
                  <FormHelperText>
                    {getLocalAndLenderError({
                      fieldName: `expenses.[0].frequency_${customerId}_${index}`,
                      doNotShowError: !!ele?.frequency || !validatedRows[index],
                    })}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={2.5}>
              <Tooltip
                title={isDisabledSharesField ? SHARE_OF_DISABLED_TOOLTIP : ""}
                placement="top"
                size="sm"
                disableHoverListener={!isDisabledSharesField}
                disableFocusListener={!isDisabledSharesField}
                disableTouchListener={!isDisabledSharesField}
              >
                <span style={{ display: "block", width: "100%" }}>
                  <TextField
                    size="small"
                    id={`expense-shareOfExpense-${ele?._id}`}
                    type="text"
                    inputProps={{ min: 0 }}
                    label="Share of"
                    variant="filled"
                    style={{ width: "100%" }}
                    value={ele?.shareOfExpense}
                    disabled={isDisabledSharesField}
                    name={`shareOfExpense_${customerId}_${index}`}
                    error={getLocalAndLenderError({
                      fieldError: requireFieldErrors[`shareOfExpense_${customerId}_${index}`],
                      fieldName: `shareOfExpense_${customerId}_${index}`,
                      localFieldName: "shareOfExpense",
                    })}
                    helperText={getLocalAndLenderError({
                      fieldError: requireFieldErrors[`shareOfExpense_${customerId}_${index}`],
                      fieldName: `shareOfExpense_${customerId}_${index}`,
                      localFieldName: "shareOfExpense",
                    })}
                    InputProps={{
                      inputComponent: PercentageFormatCustom,
                    }}
                    onChange={(event) =>
                      handle.onChange(`shareOfExpense_${customerId}_${index}`, event.target.value, ele?._id)
                    }
                    onBlur={() => handle.onBlur("shareOfExpense", ele?.shareOfExpense || 0, ele?._id, index)}
                  />
                </span>
              </Tooltip>
            </Grid>

            <Grid item xs={0.5}>
              <IconButton
                size="small"
                aria-label={`Remove expense ${index + 1}`}
                title={
                  personalFinLoaders?.removeExpenseLoader && personalFinLoaders?.id === ele?._id
                    ? "Removing..."
                    : "Remove expense"
                }
                sx={{ marginTop: "10px", color: "error.main" }}
                onClick={() => removeExpenses(ele)}
                disabled={isDisable || personalFinLoaders?.removeExpenseLoader}
              >
                <DeleteOutlineIcon fontSize="medium" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Grid item xs={12}>
        <div>
          <Button
            onClick={() => onAddNewExpenses({ expensesType: "", amount: "", frequency: "" })}
            variant="outlined"
            size="small"
            disabled={isAddExpenseLoader || isDisable}
          >
            {isAddExpenseLoader ? "Adding..." : "Add expense"}
          </Button>
        </div>
      </Grid>
    </>
  );
}
